//-------------------------------------------------------------------------------------------------- Imports
import { Card, Col, Row, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { StyleSheet } from "../../StyleSheet";
import SubDefaultLayout from "../../components/SubDefaultLayout";
import Loading from "../../components/Loading";
import { AppDispatch } from "../../stores/store";
import {
  getInternetSeguroCategories,
  getInternetSeguroUserPolicies,
} from "../../stores/slices/internetSeguroSlice";
import {
  InternetSeguroCategory,
  InternetSeguroPolicy,
  InternetSeguroUser,
  ResponseInternetSeguroAccountDetail,
} from "../../types/common";
import { describeSchedule } from "./scheduleHelpers";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- View
const ParentalControlRules = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userId } = useParams();

  const account = useSelector(
    (state: any) =>
      state.internetSeguro
        .account as ResponseInternetSeguroAccountDetail | null,
  );
  const displayUser: InternetSeguroUser | undefined = (
    account?.users ?? []
  ).find((u) => u.userId === userId);

  const categories = useSelector(
    (state: any) =>
      state.internetSeguro.categories as InternetSeguroCategory[] | null,
  );
  const categoriesLoaded = useSelector(
    (state: any) => state.internetSeguro.categoriesLoaded,
  );
  const userPolicies = useSelector(
    (state: any) =>
      state.internetSeguro.userPolicies[userId as string] as
        | Record<string, InternetSeguroPolicy>
        | undefined,
  );
  const userPoliciesLoaded = useSelector(
    (state: any) => state.internetSeguro.userPoliciesLoaded[userId as string],
  );

  useEffect(() => {
    if (userId) {
      dispatch(getInternetSeguroUserPolicies(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (!categoriesLoaded) {
      dispatch(getInternetSeguroCategories());
    }
  }, [dispatch, categoriesLoaded]);

  const isLoading = !categoriesLoaded || !userPoliciesLoaded;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <SubDefaultLayout
      title={"Reglas de usuario"}
      needReturn={true}
      navigateTo={`/parental-control/users/${userId}`}
    >
      <div style={styles.badgeContainer}>
        <Text style={styles.badgeText}>
          {displayUser?.username ?? "Usuario"}
        </Text>
      </div>
      <Text style={styles.descriptionText}>
        Las reglas configuradas se aplicarán a todos los dispositivos del
        usuario.
      </Text>
      <Text style={styles.sectionTitle}>Reglas disponibles</Text>
      <div style={styles.rulesContainer}>
        {(categories ?? []).map((category) => {
          const policy = userPolicies?.[category.categoryCode];
          const scheduleDescription = policy?.schedule
            ? describeSchedule(policy.schedule)
            : null;
          const statusText = scheduleDescription
            ? "Horarios establecidos"
            : policy?.enable
              ? "Protección activa"
              : "Sin configurar";
          const dotColor = policy?.enable ? "#52c41a" : "#d9d9d9";

          return (
            <Card
              key={category.categoryCode}
              style={styles.cardStyles}
              onClick={() =>
                navigate(
                  `/parental-control/users/${userId}/rules/${category.categoryCode}`,
                )
              }
            >
              <Row align="middle">
                <Col style={styles.dotContainer}>
                  <span
                    style={{
                      ...styles.dot,
                      backgroundColor: dotColor,
                    }}
                  />
                </Col>
                <Col flex="auto" style={styles.titleContainer}>
                  <Text style={styles.textName}>{category.displayName}</Text>
                  <Text style={styles.textSub}>{statusText}</Text>
                </Col>
                <Col>
                  <RightOutlined style={styles.iconStyles} />
                </Col>
              </Row>
            </Card>
          );
        })}
      </div>
    </SubDefaultLayout>
  );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
  badgeContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 5,
  },
  badgeText: {
    backgroundColor: "#0055c2",
    color: "white",
    borderRadius: 50,
    padding: "4px 20px",
    fontWeight: 600,
  },
  descriptionText: {
    display: "block",
    textAlign: "center",
    margin: "15px auto",
    width: "60%",
  },
  sectionTitle: {
    display: "block",
    textAlign: "center",
    fontSize: 18,
    fontWeight: 600,
    color: "#0055c1",
    marginBottom: 15,
  },
  rulesContainer: {
    width: "50%",
    margin: "0 auto",
    marginBottom: 100,
  },
  cardStyles: {
    marginBottom: 15,
    boxShadow: "0px 0px 8px #00000029",
    borderRadius: 15,
    cursor: "pointer",
  },
  dotContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingRight: 15,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  textName: {
    fontSize: "1.1em",
    fontWeight: 600,
  },
  textSub: {
    color: "#767a82",
  },
  iconStyles: {
    fontSize: 18,
    color: "#767a82",
  },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ParentalControlRules;
